<template>
  <div>

    <div class="page-header mb-6">
      <SGPBotaoVoltarComponent />
      <div>
        <h1 class="page-title">{{ moduloSelecionado.nome }}</h1>
        <span class="page-subtitle">Gerenciamento de usuários do módulo</span>
      </div>
    </div>

    <!-- Card: Associar novo usuário -->
    <v-card class="mb-6" outlined>
      <v-card-title class="section-title">
        <v-icon color="success" class="mr-2">mdi-account-plus</v-icon>
        Associar novo usuário
      </v-card-title>
      <v-divider />

      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="5">
            <SGPFormAutocompleteComponent
              v-model="novoUsuarioId"
              :listaDeOptions="usuariosDisponiveis"
              :optionLabel="'name'"
              :optionValue="'id'"
              placeholder="Selecionar usuário"
              name="usuario"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <SGPFormAutocompleteComponent
              v-model="novaRole"
              :listaDeOptions="listaDePerfis"
              :optionLabel="'name'"
              :optionValue="'id'"
              placeholder="Selecionar role"
              name="novaRole"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-btn
              depressed
              color="primary"
              :disabled="!novoUsuarioId"
              @click="adicionarUsuario"
              block
            >
              <v-icon left>mdi-plus</v-icon>
              Associar
            </v-btn>
          </v-col>
        </v-row>

        <div v-if="!usuariosDisponiveis.length && !usuariosAssociados.length" class="text-center text--secondary mt-2">
          Nenhum usuário disponível para associar.
        </div>
        <div v-else-if="!usuariosDisponiveis.length" class="text-caption text--secondary mt-1">
          Todos os usuários já estão associados a este módulo.
        </div>
      </v-card-text>
    </v-card>

    <!-- Card: Usuários associados -->
    <v-card outlined>
      <v-card-title class="section-title">
        <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
        Usuários associados
        <v-chip small class="ml-3" color="primary" outlined>{{ usuariosAssociados.length }}</v-chip>
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-0">
        <v-simple-table v-if="usuariosAssociados.length">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left pl-4">Usuário</th>
                <th class="text-left">Role</th>
                <th class="text-right pr-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="usuario in usuariosAssociados" :key="usuario.id" class="table-row">
                <td class="pl-4">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="mr-3">
                      <span class="white--text caption font-weight-bold">
                        {{ usuario.name.charAt(0).toUpperCase() }}
                      </span>
                    </v-avatar>
                    <span class="font-weight-medium">{{ usuario.name }}</span>
                  </div>
                </td>
                <td style="width: 260px;">
                  <SGPFormAutocompleteComponent
                    v-model="usuario.pivot_role_id"
                    :listaDeOptions="listaDePerfis"
                    :optionLabel="'name'"
                    :optionValue="'id'"
                    placeholder="Selecionar role"
                    name="role"
                  />
                </td>
                <td class="text-right pr-4" style="width: 160px; white-space: nowrap;">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="salvarRole(usuario)"
                      >
                        <v-icon>mdi-content-save</v-icon>
                      </v-btn>
                    </template>
                    <span>Salvar role</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        @click="removerUsuario(usuario)"
                      >
                        <v-icon>mdi-account-remove</v-icon>
                      </v-btn>
                    </template>
                    <span>Remover usuário</span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div v-else class="pa-6 text-center text--secondary">
          <v-icon size="48" color="grey lighten-2">mdi-account-off-outline</v-icon>
          <p class="mt-2 mb-0">Nenhum usuário associado a este módulo.</p>
        </div>
      </v-card-text>
    </v-card>

    <SGPLoadingComponent v-if="loadingModulo" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ModuloUsuarioAssociacaoPage',

  data() {
    return {
      novoUsuarioId: null,
      novaRole: null
    }
  },

  async created() {
    await Promise.all([
      this.listarUsuariosParaModulo(),
      this.listarPerfis(),
      this.recuperarModuloComUsuarios(this.$route.params.modulo)
    ])
  },

  computed: {
    ...mapGetters('ctrlModulos', ['loadingModulo', 'moduloSelecionado', 'listaDeUsuarios']),
    ...mapGetters('perfis', ['listaDePerfis']),

    usuariosAssociados() {
      const users = this.moduloSelecionado?.usuarios || []
      return users.map(u => ({ ...u, pivot_role_id: u.pivot?.role_id || null }))
    },

    usuariosDisponiveis() {
      const associadosIds = this.usuariosAssociados.map(u => u.id)
      const todos = this.listaDeUsuarios?.data || []
      return todos.filter(u => !associadosIds.includes(u.id))
    }
  },

  methods: {
    ...mapActions('ctrlModulos', [
      'listarUsuariosParaModulo',
      'recuperarModuloComUsuarios',
      'associarUsuarioAModulo',
      'atualizarRoleUsuario',
      'desassociarUsuario'
    ]),
    ...mapActions('perfis', ['listarPerfis']),

    async adicionarUsuario() {
      await this.associarUsuarioAModulo({
        moduloId: this.$route.params.modulo,
        user_id: this.novoUsuarioId,
        role_id: this.novaRole || null
      })
      this.novoUsuarioId = null
      this.novaRole = null
      await this.recuperarModuloComUsuarios(this.$route.params.modulo)
    },

    async salvarRole(usuario) {
      await this.atualizarRoleUsuario({
        moduloId: this.$route.params.modulo,
        userId: usuario.id,
        role_id: usuario.pivot_role_id
      })
      await this.recuperarModuloComUsuarios(this.$route.params.modulo)
    },

    async removerUsuario(usuario) {
      await this.desassociarUsuario({
        moduloId: this.$route.params.modulo,
        userId: usuario.id
      })
      await this.recuperarModuloComUsuarios(this.$route.params.modulo)
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.page-subtitle {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.54);
}

.section-title {
  font-size: 1rem !important;
  font-weight: 600 !important;
  padding: 16px !important;
}

.table-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
